/*
 * civili.systems — redesign v2.
 *
 * Direction: horizontal bands, not a stack of paragraphs. The page is built from full-bleed rows
 * that run edge to edge: a moving state ticker under the headline (the subtitle is a bar, not a
 * sentence), a side-scrolling rail of what CIVILI actually built, four layer rows that invert, and
 * a method strip split exactly where the invoice starts.
 *
 * Strictly achromatic, so energy comes from scale, inversion and horizontal movement. Square
 * corners: the identity is monolithic, nothing here is soft. No media anywhere on the page.
 */

@font-face {
  font-family: 'Archivo';
  src: url('@fontsource-variable/archivo/files/archivo-latin-wght-normal.woff2')
    format('woff2-variations');
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'IBM Plex Mono';
  src: url('@fontsource/ibm-plex-mono/files/ibm-plex-mono-latin-400-normal.woff2')
    format('woff2');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

:root {
  --paper: #f2f2f2;
  --ink: #101010;
  --black: #000;
  --white: #fff;
  --steel: #444;
  --mist: #e4e4e4;
  --line: color-mix(in srgb, var(--ink) 18%, transparent);
  --line-strong: color-mix(in srgb, var(--ink) 36%, transparent);
  --line-inverse: color-mix(in srgb, var(--white) 26%, transparent);
  --page: clamp(20px, 4.5vw, 64px);
  --header-height: 64px;
  --mono: 'IBM Plex Mono', ui-monospace, 'SF Mono', monospace;
  --sans: 'Archivo', Arial, Helvetica, sans-serif;
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  scroll-padding-top: calc(var(--header-height) + 16px);
  background: var(--paper);
}

body {
  margin: 0;
  min-width: 320px;
  overflow-x: clip;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
  font-size: 16px;
  line-height: 1.5;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
}

button,
input,
textarea,
select {
  font: inherit;
  color: inherit;
}

button,
a {
  -webkit-tap-highlight-color: transparent;
}

a {
  color: inherit;
}

img {
  display: block;
  max-width: 100%;
}

::selection {
  background: var(--ink);
  color: var(--white);
}

:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 3px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.skip-link {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 2000;
  padding: 10px 14px;
  transform: translateY(-180%);
  background: var(--ink);
  color: var(--white);
  font: 12px/1 var(--mono);
  text-decoration: none;
  transition: transform 160ms ease;
}

.skip-link:focus {
  transform: translateY(0);
}

/* A label that is a horizontal bar: the words sit at the left, the rule runs to the far edge. */
.rule-label {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 18px;
  margin: 0;
  font: 11px/1 var(--mono);
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--steel);
  white-space: nowrap;
}

.rule-label::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--line-strong);
}

/* ---------------------------------------------------------------- header */

.site-header {
  position: sticky;
  top: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  gap: 28px;
  height: var(--header-height);
  padding-inline: var(--page);
  background: color-mix(in srgb, var(--paper) 90%, transparent);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid transparent;
  transition: border-color 200ms ease;
}

.site-header[data-scrolled] {
  border-bottom-color: var(--line-strong);
}

.brand-lockup {
  display: block;
  flex-shrink: 0;
}

.brand-lockup img {
  width: clamp(100px, 11vw, 124px);
  height: auto;
}

.site-nav {
  display: none;
  gap: 26px;
  margin-inline: auto;
}

.site-nav a {
  font: 12px/1.4 var(--mono);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: var(--steel);
  border-bottom: 1px solid transparent;
  padding-block: 4px;
}

.site-nav a:hover,
.site-nav a[aria-current] {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.site-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.icon-control {
  display: grid;
  place-items: center;
  width: 34px;
  height: 32px;
  border: 1px solid var(--line-strong);
  background: none;
  cursor: pointer;
  font-size: 10px;
  line-height: 1;
}

.icon-control:hover {
  background: var(--ink);
  color: var(--paper);
  border-color: var(--ink);
}

.lang-control {
  padding: 8px 10px;
  border: 1px solid var(--line-strong);
  background: none;
  cursor: pointer;
  font: 12px/1 var(--mono);
  letter-spacing: 0.1em;
}

.lang-control:hover {
  background: var(--ink);
  color: var(--white);
  border-color: var(--ink);
}

.header-cta {
  padding: 9px 16px;
  background: var(--ink);
  color: var(--white);
  font: 12px/1.2 var(--mono);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
}

.header-cta:hover {
  background: var(--black);
}

@media (min-width: 860px) {
  .site-nav {
    display: flex;
  }
}

/* ---------------------------------------------------------------- shared */

.section-title {
  margin: 0;
  max-width: 28ch;
  font-size: clamp(1.8rem, 4.4vw, 3.2rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.02em;
}

.section-title em,
.hero__title em {
  font-style: normal;
  background: var(--ink);
  color: var(--paper);
  padding: 0 0.14em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

.action {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 24px;
  font: 600 15px/1.2 var(--sans);
  text-decoration: none;
  cursor: pointer;
  border: 1px solid var(--ink);
  white-space: nowrap;
  transition: background 160ms ease, color 160ms ease;
}

.action--solid {
  background: var(--ink);
  color: var(--white);
}

.action--solid:hover {
  background: var(--paper);
  color: var(--ink);
}

.action--line {
  background: none;
  color: var(--ink);
}

.action--line:hover {
  background: var(--ink);
  color: var(--white);
}

[data-reveal] {
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 560ms var(--ease-out), transform 560ms var(--ease-out);
}

[data-reveal][data-visible] {
  opacity: 1;
  transform: none;
}

/* ------------------------------------------------------------------ hero */

.hero {
  display: flex;
  flex-direction: column;
  gap: clamp(26px, 4vw, 44px);
  padding-block: clamp(44px, 8vh, 88px) clamp(32px, 5vh, 56px);
}

.hero__title {
  margin: 0;
  padding-inline: var(--page);
  font-size: clamp(2.5rem, 8.2vw, 7.2rem);
  font-weight: 800;
  line-height: 0.94;
  letter-spacing: -0.035em;
}

.hero__title span,
.hero__title em {
  display: block;
  width: fit-content;
}

/* --------------------------------------------------------------- ticker */

.ticker {
  background: var(--ink);
  color: var(--paper);
  border-block: 1px solid var(--black);
}

.ticker__viewport {
  display: flex;
  overflow: hidden;
  user-select: none;
}

.ticker__track {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: clamp(28px, 4vw, 64px);
  padding-block: clamp(14px, 1.8vw, 20px);
  padding-right: clamp(28px, 4vw, 64px);
  animation: ticker-travel 42s linear infinite;
}

.ticker__item {
  font: clamp(12px, 1.35vw, 15px) / 1.3 var(--mono);
  white-space: nowrap;
}

.ticker__item::after {
  content: '/';
  margin-left: clamp(28px, 4vw, 64px);
  color: color-mix(in srgb, var(--paper) 45%, transparent);
}

@keyframes ticker-travel {
  to {
    transform: translateX(-100%);
  }
}

.hero__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding-inline: var(--page);
}

.hero__kicker {
  margin: 0;
  max-width: 34ch;
  font-size: clamp(1.05rem, 1.7vw, 1.35rem);
  font-weight: 640;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* ------------------------------------------------------------- showcases */

/*
 * One band per product, and the band is mostly the demo. The argument for these two is not a
 * bullet list: it is watching them work, which is what the animated stages do.
 */
.showcase {
  display: grid;
  gap: clamp(28px, 4vw, 56px);
  align-items: center;
  padding: clamp(44px, 7vh, 88px) var(--page);
  border-top: 1px solid var(--line);
}

.showcase[data-tone='ink'] {
  background: var(--ink);
  color: var(--paper);
  border-top-color: var(--black);
}

.showcase[data-tone='ink'] .rule-label {
  color: color-mix(in srgb, var(--paper) 68%, transparent);
}

.showcase[data-tone='ink'] .rule-label::after {
  background: var(--line-inverse);
}

.showcase__copy {
  max-width: 44ch;
}

.showcase__name {
  margin: 0;
  font-size: clamp(2.6rem, 6vw, 4.4rem);
  font-weight: 800;
  line-height: 0.95;
  letter-spacing: -0.03em;
}

.showcase__line {
  margin: 14px 0 0;
  font-size: clamp(1.15rem, 2vw, 1.5rem);
  font-weight: 640;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.showcase__limit {
  margin: 28px 0 0;
  padding-top: 16px;
  border-top: 1px solid currentColor;
  max-width: 34ch;
  font-size: 0.94rem;
  font-weight: 620;
}

.showcase__limit span {
  display: block;
  margin-bottom: 6px;
  font: 10px/1 var(--mono);
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.66;
}

/* The band label runs the full width above both columns, so the bar reaches the far edge. */
.showcase > .rule-label {
  margin-bottom: clamp(18px, 2.5vw, 28px);
}

@media (min-width: 940px) {
  .showcase {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.05fr);
  }

  .showcase > .rule-label {
    grid-column: 1 / -1;
    margin-bottom: 0;
  }
}

/* ------------------------------------------------------------------ demos */

.demo {
  margin: 0;
  width: 100%;
  max-width: 540px;
  justify-self: end;
  background: var(--paper);
  color: var(--ink);
  border: 1px solid var(--black);
}

.demo--dossier {
  background: var(--white);
  border-color: var(--line-strong);
}

.demo__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line);
  font: 10px/1 var(--mono);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel);
}

.demo__tag {
  padding: 4px 8px;
  border: 1px solid var(--line-strong);
}

/*
 * Every beat is either absent or fully legible, never fading through a low-contrast state: text
 * that crosses 40% opacity on its way in is unreadable for the moment it takes, and a scene that
 * loops would do that all day. So opacity and visibility switch instantly and only the small
 * offset animates, which is also how a message actually lands in a chat.
 */
.chat__line,
.dossier__label,
.dossier__evidence li,
.dossier__verdict,
.dossier__draft,
.dossier__gate,
.chat__event {
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px);
  transition: transform 360ms var(--ease-out);
}

.chat__line[data-on],
.dossier__label[data-on],
.dossier__evidence li[data-on],
.dossier__verdict[data-on],
.dossier__draft[data-on],
.dossier__gate[data-on],
.chat__event[data-on] {
  opacity: 1;
  visibility: visible;
  transform: none;
}

/* ---- ATRIO: the conversation */

.chat {
  display: grid;
  gap: 14px;
  align-content: start;
  padding: 20px 16px;
  min-height: 320px;
}

.chat__line {
  display: grid;
  gap: 5px;
  justify-items: start;
  margin: 0;
}

.chat__line--out {
  justify-items: end;
  text-align: right;
}

.chat__who {
  font: 9px/1 var(--mono);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel);
}

.chat__bubble {
  max-width: 88%;
  padding: 11px 14px;
  background: var(--mist);
  font-size: 0.96rem;
  line-height: 1.4;
  text-align: left;
}

.chat__line--out .chat__bubble {
  background: var(--ink);
  color: var(--paper);
}

.chat__note {
  font: 10px/1.4 var(--mono);
  color: var(--steel);
}

/* The typing indicator leaves the flow entirely when it is not its beat, so nothing reserves an
   empty row between the question and the answer. */
.chat__typing {
  display: none;
}

.chat__typing[data-on] {
  display: grid;
}

.chat__typing .chat__bubble {
  display: flex;
  gap: 5px;
  padding: 14px;
}

.chat__typing i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--paper) 72%, transparent);
  animation: chat-blink 1s ease-in-out infinite;
}

.chat__typing i:nth-child(2) {
  animation-delay: 0.16s;
}

.chat__typing i:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes chat-blink {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

/* The point of the whole scene: the message it refuses to answer. */
.chat__event {
  margin: 2px 0 0;
  padding: 12px 14px;
  border: 1px solid var(--ink);
  background: var(--paper);
  font: 11.5px/1.5 var(--mono);
  font-weight: 500;
}

/* ---- CANTERA: the dossier */

.dossier {
  display: grid;
  gap: 11px;
  align-content: start;
  padding: 20px 16px;
  min-height: 320px;
}

.dossier__label {
  margin: 0;
  font: 10px/1 var(--mono);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel);
}

.dossier__evidence {
  display: grid;
  gap: 9px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.dossier__evidence li {
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 9px;
  font-size: 0.95rem;
}

.dossier__evidence li::before {
  content: '+';
  color: var(--steel);
  font-family: var(--mono);
}

.dossier__verdict {
  display: grid;
  gap: 5px;
  margin: 6px 0 0;
  padding: 13px 14px;
  background: var(--ink);
  color: var(--paper);
}

.dossier__verdict strong {
  font: 10px/1 var(--mono);
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--paper) 74%, transparent);
}

.dossier__verdict span {
  font-size: 0.98rem;
  font-weight: 620;
}

.dossier__draft {
  margin: 0;
  padding: 13px 14px;
  border: 1px solid var(--line-strong);
  font-size: 0.92rem;
  color: var(--steel);
}

.dossier__draft-label {
  display: block;
  margin-bottom: 6px;
  font: 10px/1 var(--mono);
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.dossier__gate {
  margin: 2px 0 0;
  padding: 12px 14px;
  border: 1px solid var(--ink);
  font-size: 0.94rem;
  font-weight: 700;
}

/* ---------------------------------------------------------------- layers */

.layers {
  padding-block: clamp(40px, 7vh, 84px) 0;
  border-top: 1px solid var(--line);
}

.layers .rule-label,
.layers .section-title {
  padding-inline: var(--page);
}

.layers .rule-label {
  margin-bottom: clamp(20px, 3vw, 32px);
}

.layers .section-title {
  margin-bottom: clamp(24px, 3.5vw, 44px);
}

.layer-rows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.layer-row {
  display: grid;
  gap: 6px 24px;
  align-items: baseline;
  padding: clamp(20px, 2.4vw, 28px) var(--page);
  border-top: 1px solid var(--line);
  transition: background 180ms ease, color 180ms ease;
}

.layer-row:last-child {
  border-bottom: 1px solid var(--line);
}

.layer-row:hover {
  background: var(--ink);
  color: var(--paper);
}

/* These read as secondary but must still pass contrast, so they carry a real colour rather than a
   dimmed opacity, and swap explicitly when the row inverts. */
.layer-row__index {
  font: 11px/1.6 var(--mono);
  letter-spacing: 0.1em;
  color: var(--steel);
}

.layer-row__name {
  margin: 0;
  font-size: clamp(1.2rem, 2.2vw, 1.75rem);
  font-weight: 740;
  line-height: 1.1;
  letter-spacing: -0.015em;
}

.layer-row__body {
  margin: 0;
  font-size: 0.98rem;
  color: var(--steel);
}

.layer-row__limit {
  margin: 0;
  font-size: 0.94rem;
  font-weight: 620;
}

.layer-row__limit span {
  display: block;
  margin-bottom: 4px;
  font: 10px/1 var(--mono);
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel);
}

.layer-row:hover .layer-row__index,
.layer-row:hover .layer-row__body,
.layer-row:hover .layer-row__limit span {
  color: color-mix(in srgb, var(--paper) 78%, transparent);
}

@media (min-width: 1000px) {
  .layer-row {
    grid-template-columns: 3rem minmax(11ch, 1fr) minmax(0, 1.25fr) minmax(0, 1.15fr);
    align-items: start;
  }
}

/* ---------------------------------------------------------------- method */

.method {
  padding-block: clamp(48px, 8vh, 96px);
  padding-inline: var(--page);
  background: var(--ink);
  color: var(--paper);
}

.method .rule-label {
  margin-bottom: clamp(20px, 3vw, 32px);
  color: color-mix(in srgb, var(--paper) 66%, transparent);
}

.method .rule-label::after {
  background: var(--line-inverse);
}

.method__bar {
  display: flex;
  margin: clamp(26px, 3.5vw, 44px) 0 0;
  height: 44px;
  font: 10px/1 var(--mono);
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.method__bar span {
  display: flex;
  align-items: center;
  padding-inline: 16px;
}

.method__bar-free {
  flex: 1;
  background: var(--paper);
  color: var(--ink);
}

.method__bar-paid {
  flex: 1;
  border: 1px solid var(--line-inverse);
  border-left: 0;
  color: color-mix(in srgb, var(--paper) 78%, transparent);
}

.method__steps {
  display: grid;
  gap: 1px;
  margin: 1px 0 0;
  padding: 0;
  list-style: none;
  background: var(--line-inverse);
  border: 1px solid var(--line-inverse);
  border-top: 0;
}

.method-step {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: clamp(18px, 2.2vw, 26px);
  background: var(--ink);
}

.method-step__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 4px;
  font: 10px/1 var(--mono);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--paper) 62%, transparent);
}

.method-step__meta strong {
  font-weight: 400;
}

.method-step h3 {
  margin: 0;
  font-size: clamp(1.1rem, 1.7vw, 1.3rem);
  font-weight: 740;
  letter-spacing: -0.01em;
}

.method-step p:last-child {
  margin: 0;
  font-size: 0.95rem;
  color: color-mix(in srgb, var(--paper) 74%, transparent);
}

.method__after {
  margin: 22px 0 0;
  max-width: 62ch;
  font: 12px/1.7 var(--mono);
  color: color-mix(in srgb, var(--paper) 70%, transparent);
}

@media (min-width: 760px) {
  .method__steps {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* --------------------------------------------------------------- contact */

.contact {
  display: grid;
  gap: clamp(32px, 4.5vw, 64px);
  padding: clamp(48px, 8vh, 96px) var(--page);
}

.contact__terms {
  margin: 22px 0 0;
  font: 12px/1.8 var(--mono);
  letter-spacing: 0.04em;
  color: var(--steel);
  max-width: 46ch;
}

.contact__direct {
  margin: 16px 0 0;
  font: 13px/1.7 var(--mono);
  color: var(--steel);
}

.contact__direct a {
  color: var(--ink);
  font-weight: 600;
  text-underline-offset: 3px;
}

.contact__mark {
  width: 52px;
  height: 52px;
  margin-top: 32px;
}

@media (min-width: 1000px) {
  .contact {
    grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
    align-items: start;
  }
}

/* ------------------------------------------------------------- lead form */

.lead-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
  padding: clamp(20px, 2.6vw, 32px);
  border: 1px solid var(--line-strong);
  background: var(--white);
}

.lead-form__summary {
  font: 13px/1.5 var(--mono);
}

.lead-form__summary:not(:empty) {
  padding: 12px 14px;
  border: 1px solid var(--ink);
  background: var(--mist);
}

.form-kicker {
  margin: 0;
  font: 10px/1.4 var(--mono);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel);
}

.lead-form__review p:last-child {
  margin: 6px 0 0;
  font-size: 0.88rem;
  color: var(--steel);
}

.lead-form__heading {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.lead-form__heading span {
  font: 11px/1 var(--mono);
  color: var(--steel);
}

.lead-form__heading h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 760;
  letter-spacing: -0.01em;
}

.lead-form__fields {
  display: grid;
  gap: 16px;
}

@media (min-width: 620px) {
  .lead-form__fields {
    grid-template-columns: 1fr 1fr;
  }

  .lead-field--wide {
    grid-column: 1 / -1;
  }
}

.lead-form__fields > *,
.contact > * {
  min-width: 0;
}

.lead-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.lead-field__label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font: 11px/1.4 var(--mono);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--steel);
}

.lead-field__optional {
  font-style: normal;
  text-transform: none;
  letter-spacing: 0.04em;
  color: color-mix(in srgb, var(--steel) 72%, transparent);
}

.lead-field input,
.lead-field select,
.lead-field textarea {
  width: 100%;
  min-width: 0;
  padding: 11px 12px;
  border: 1px solid var(--line-strong);
  background: var(--white);
  border-radius: 0;
}

.lead-field textarea {
  resize: vertical;
  min-height: 104px;
}

.lead-field input:focus-visible,
.lead-field select:focus-visible,
.lead-field textarea:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 0;
}

.lead-field [aria-invalid='true'] {
  border-color: var(--ink);
  background: var(--mist);
}

.lead-field__hint {
  font-size: 0.79rem;
  color: var(--steel);
}

[data-field-error] {
  font: 11px/1.4 var(--mono);
}

[data-field-error]:empty {
  display: none;
}

.lead-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9rem;
}

.lead-consent input {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: var(--ink);
}

.lead-consent[data-invalid] {
  outline: 1px solid var(--ink);
  outline-offset: 6px;
}

.lead-form__trap {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.lead-form__privacy {
  margin: 0;
  font-size: 0.8rem;
  color: var(--steel);
}

.lead-form__privacy a {
  color: var(--ink);
}

.lead-form__action .action {
  width: 100%;
  justify-content: center;
}

.lead-form__note {
  margin: 0;
  font: 11px/1.6 var(--mono);
  color: var(--steel);
}

.lead-form__receipt {
  gap: 12px;
}

.lead-form__receipt h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 760;
}

.lead-form__receipt p {
  margin: 0;
  color: var(--steel);
}

.lead-form__receipt-id {
  font: 13px/1.5 var(--mono);
  color: var(--ink);
}

/* ---------------------------------------------------------------- footer */

.site-footer {
  display: grid;
  gap: 22px;
  padding: clamp(34px, 6vh, 60px) var(--page) 28px;
  border-top: 1px solid var(--line-strong);
}

.site-footer__brand img {
  width: 110px;
  height: auto;
}

.site-footer__brand p {
  margin: 10px 0 0;
  font-weight: 640;
}

.site-footer__meta {
  display: grid;
  gap: 6px;
}

.site-footer__meta p {
  margin: 0;
  font-size: 0.84rem;
  color: var(--steel);
}

.social-nav,
.legal-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
}

.social-nav a,
.legal-nav a {
  font: 12px/1.5 var(--mono);
  letter-spacing: 0.06em;
  color: var(--steel);
  text-decoration: none;
  border-bottom: 1px solid var(--line);
  padding-bottom: 2px;
}

.social-nav a:hover,
.legal-nav a:hover {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.site-footer__status {
  margin: 0;
  font: 11px/1.5 var(--mono);
  color: var(--steel);
}

@media (min-width: 900px) {
  .site-footer {
    grid-template-columns: 1fr auto;
    align-items: end;
  }

  .site-footer__brand {
    grid-row: 1 / 3;
  }
}

/* -------------------------------------------------------------- noscript */

.civili-noscript {
  max-width: 640px;
  margin: 80px auto;
  padding: 0 24px;
  font-family: var(--sans);
}

/* -------------------------------------------------------- reduced motion */

[data-motion='paused'] .ticker__track,
[data-motion='paused'] .chat__typing i {
  animation-play-state: paused;
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  [data-reveal] {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .ticker__track {
    animation: none;
  }

}
